import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import TitleTooltip from 'components/v2/common/titleTooltip';

import api from 'api';
import { Role } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type {
    OwnershipScope,
    ProviderOption,
    ServiceFormValues,
    ServiceRecord,
} from 'types/servicesDocuments';
import { buildServiceWritePayload } from 'utils/servicesDocuments';
import {
    serviceFormInitialValues,
    serviceFormValidationSchema,
} from 'utils/validations/servicesDocuments';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void | Promise<void>;
    initialData?: ServiceRecord | null;
    userRole: string;
    defaultScope: OwnershipScope;
    defaultProviderId: number | '';
    providerOptions: ProviderOption[];
}

const mapInitialValues = (
    initialData: ServiceRecord | null | undefined,
    defaults: Pick<ServiceFormValues, 'scope' | 'service_provider_id'>
): ServiceFormValues => ({
    ...serviceFormInitialValues,
    ...defaults,
    service_code: initialData?.service_code || '',
    country_code: initialData?.country_code || '',
    name_en: initialData?.name_en || '',
    name_fr: initialData?.name_fr || '',
    name_es: initialData?.name_es || '',
    is_active: initialData?.is_active ?? true,
    show_in_report: initialData?.show_in_report ?? true,
});

const ServiceFormDialog = ({
    open,
    onClose,
    onSuccess,
    initialData,
    userRole,
    defaultScope,
    defaultProviderId,
    providerOptions,
}: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [submitting, setSubmitting] = useState(false);
    const isEditMode = Boolean(initialData?.id);
    const isSuperSuperAdmin = userRole === Role.SUPER_SUPER_ADMIN;

    const initialValues = useMemo(
        () =>
            mapInitialValues(initialData, {
                scope: isEditMode
                    ? initialData?.service_provider_id == null
                        ? 'universal'
                        : 'provider'
                    : defaultScope,
                service_provider_id: isEditMode
                    ? (initialData?.service_provider_id ?? '')
                    : defaultProviderId,
            }),
        [defaultProviderId, defaultScope, initialData, isEditMode]
    );

    const handleSubmit = async (values: ServiceFormValues) => {
        try {
            setSubmitting(true);
            if (isEditMode && initialData?.id) {
                const {
                    scope: _scope,
                    service_provider_id: _sp,
                    ...patchBody
                } = buildServiceWritePayload(values);
                await api.services.updateService(initialData.id, patchBody);
            } else {
                await api.services.createService(buildServiceWritePayload(values));
            }
            dispatch(setAlert({ message: translate('saveChanges'), type: 'success' }));
            await onSuccess();
            onClose();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={serviceFormValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue }) => (
                    <Box component={Form}>
                        <DialogTitle>
                            <TitleTooltip
                                title={translate(
                                    isEditMode
                                        ? 'servicesDocuments.services.editTitle'
                                        : 'servicesDocuments.services.addTitle'
                                )}
                            />
                        </DialogTitle>
                        <DialogContent dividers>
                            <Stack spacing={1.25}>
                                {!isEditMode && isSuperSuperAdmin ? (
                                    <>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="service-form-scope-label">
                                                {translate('servicesDocuments.scope.ownershipAria')}
                                            </InputLabel>
                                            <Select
                                                labelId="service-form-scope-label"
                                                label={translate(
                                                    'servicesDocuments.scope.ownershipAria'
                                                )}
                                                value={values.scope}
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        'scope',
                                                        event.target.value as OwnershipScope
                                                    )
                                                }
                                            >
                                                <MenuItem value="provider">
                                                    {translate('servicesDocuments.scope.provider')}
                                                </MenuItem>
                                                <MenuItem value="universal">
                                                    {translate('servicesDocuments.scope.universal')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        {values.scope === 'provider' ? (
                                            <FormField
                                                type="autosuggest"
                                                name="service_provider_id"
                                                label="servicesDocuments.scope.selectProvider"
                                                options={providerOptions.map((option) => ({
                                                    label: option.name,
                                                    value: option.id,
                                                }))}
                                                required
                                                disabled={submitting}
                                            />
                                        ) : null}
                                    </>
                                ) : null}

                                <FormField
                                    size="small"
                                    type="select"
                                    name="country_code"
                                    label="servicesDocuments.fields.country"
                                    options={[
                                        { label: 'CA', value: 'CA' },
                                        { label: 'AU', value: 'AU' },
                                        { label: 'UK', value: 'UK' },
                                    ]}
                                    required
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    name="service_code"
                                    label="servicesDocuments.fields.serviceCode"
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    name="name_en"
                                    label="servicesDocuments.fields.nameEn"
                                    required
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    name="name_fr"
                                    label="servicesDocuments.fields.nameFr"
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    name="name_es"
                                    label="servicesDocuments.fields.nameEs"
                                    disabled={submitting}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.is_active}
                                            onChange={(_, checked) =>
                                                setFieldValue('is_active', checked)
                                            }
                                        />
                                    }
                                    label={translate('servicesDocuments.fields.active')}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.show_in_report}
                                            onChange={(_, checked) =>
                                                setFieldValue('show_in_report', checked)
                                            }
                                        />
                                    }
                                    label={translate('servicesDocuments.fields.showInReport')}
                                />
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="inherit" disabled={submitting}>
                                {translate('cancel')}
                            </Button>
                            <SubmitButton loading={submitting}>save</SubmitButton>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
};

export default ServiceFormDialog;
